import { useState } from "react";
import axios from "axios";
import { X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { OW_API } from "../../lib/operatorWalk";

function formatNumber(value) {
  if (value === null || value === undefined || value === "") return "";
  const v = Number(value);
  if (Number.isNaN(v)) return "";
  if (v === Math.trunc(v)) return v.toLocaleString("en-US");
  return v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function renderDisplay({ display_template, unit, low_value, high_value }) {
  const t = display_template || "";
  return t
    .split("{low}").join(formatNumber(low_value))
    .split("{high}").join(formatNumber(high_value))
    .split("{unit}").join(unit || "")
    .trim();
}

export default function PricingEditModal({ entry, authHeader, unitPresets, priceTypes, onClose, onSaved }) {
  const [form, setForm] = useState({
    service_display_name: entry.service_display_name || "",
    label: entry.label || "",
    price_type: entry.price_type || "single",
    low_value: entry.low_value ?? "",
    high_value: entry.high_value ?? "",
    unit: entry.unit || "",
    display_template: entry.display_template || "",
    internal_notes: entry.internal_notes || "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const preview = renderDisplay(form);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        service_display_name: form.service_display_name,
        label: form.label,
        price_type: form.price_type,
        unit: form.unit,
        display_template: form.display_template,
        internal_notes: form.internal_notes,
      };
      if (form.low_value === "" || form.low_value === null) {
        payload.clear_low = true;
      } else {
        payload.low_value = Number(form.low_value);
      }
      if (form.high_value === "" || form.high_value === null) {
        payload.clear_high = true;
      } else {
        payload.high_value = Number(form.high_value);
      }
      await axios.patch(`${OW_API}/admin/pricing/${entry.id}`, payload, { headers: authHeader });
      toast.success("Price updated.");
      onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not save this price. Please review the values.";
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-brand-900/50 flex items-center justify-center p-4" data-testid="pricing-edit-modal">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-brand-200 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-brand-200 sticky top-0 bg-white">
          <div className="leading-tight">
            <div className="text-[10px] tracking-[0.25em] uppercase text-brand-400">{entry.service_display_name}</div>
            <h3 className="font-serif text-xl text-brand-700">Edit price</h3>
          </div>
          <button onClick={onClose} className="text-brand-400 hover:text-brand-700 transition-colors" data-testid="pricing-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs tracking-wide uppercase text-brand-500">Service name</span>
              <input
                value={form.service_display_name}
                onChange={(e) => set("service_display_name", e.target.value)}
                className="mt-1.5 w-full border border-brand-200 px-3 py-2 text-sm focus:outline-none focus:border-brand-500"
                data-testid="pricing-field-service-name"
              />
            </label>
            <label className="block">
              <span className="text-xs tracking-wide uppercase text-brand-500">Label</span>
              <input
                value={form.label}
                onChange={(e) => set("label", e.target.value)}
                className="mt-1.5 w-full border border-brand-200 px-3 py-2 text-sm focus:outline-none focus:border-brand-500"
                data-testid="pricing-field-label"
              />
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <label className="block">
              <span className="text-xs tracking-wide uppercase text-brand-500">Price type</span>
              <select
                value={form.price_type}
                onChange={(e) => set("price_type", e.target.value)}
                className="mt-1.5 w-full border border-brand-200 px-3 py-2 text-sm bg-white focus:outline-none focus:border-brand-500"
                data-testid="pricing-field-type"
              >
                {priceTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs tracking-wide uppercase text-brand-500">Low value</span>
              <input
                type="number"
                value={form.low_value}
                onChange={(e) => set("low_value", e.target.value)}
                placeholder="optional"
                className="mt-1.5 w-full border border-brand-200 px-3 py-2 text-sm focus:outline-none focus:border-brand-500"
                data-testid="pricing-field-low"
              />
            </label>
            <label className="block">
              <span className="text-xs tracking-wide uppercase text-brand-500">High value</span>
              <input
                type="number"
                value={form.high_value}
                onChange={(e) => set("high_value", e.target.value)}
                placeholder="optional"
                className="mt-1.5 w-full border border-brand-200 px-3 py-2 text-sm focus:outline-none focus:border-brand-500"
                data-testid="pricing-field-high"
              />
            </label>
          </div>

          <label className="block">
            <span className="text-xs tracking-wide uppercase text-brand-500">Unit</span>
            <input
              value={form.unit}
              onChange={(e) => set("unit", e.target.value)}
              list="pricing-unit-presets"
              placeholder="e.g. per month"
              className="mt-1.5 w-full border border-brand-200 px-3 py-2 text-sm focus:outline-none focus:border-brand-500"
              data-testid="pricing-field-unit"
            />
            <datalist id="pricing-unit-presets">
              {unitPresets.map((u) => (
                <option key={u} value={u} />
              ))}
            </datalist>
          </label>

          <label className="block">
            <span className="text-xs tracking-wide uppercase text-brand-500">Display template</span>
            <input
              value={form.display_template}
              onChange={(e) => set("display_template", e.target.value)}
              className="mt-1.5 w-full border border-brand-200 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand-500"
              data-testid="pricing-field-template"
            />
            <span className="mt-1.5 block text-xs text-ink-muted">
              Use placeholders: {"{low}"}, {"{high}"}, {"{unit}"}. Put literal $ and % directly in the template.
            </span>
          </label>

          <label className="block">
            <span className="text-xs tracking-wide uppercase text-brand-500">Internal notes</span>
            <textarea
              value={form.internal_notes}
              onChange={(e) => set("internal_notes", e.target.value)}
              rows={2}
              className="mt-1.5 w-full border border-brand-200 px-3 py-2 text-sm focus:outline-none focus:border-brand-500"
              data-testid="pricing-field-notes"
            />
          </label>

          <div className="bg-brand-100 border border-brand-200 px-4 py-4">
            <div className="text-[10px] tracking-[0.25em] uppercase text-brand-500 mb-2">Live preview</div>
            <div className="font-serif text-2xl text-brand-700" data-testid="pricing-preview">
              {preview || <span className="text-ink-muted text-base">Add values to see the rendered price.</span>}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-brand-200 sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-sm text-brand-700 border border-brand-200 hover:border-brand-500 transition-colors"
            data-testid="pricing-modal-cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-brand-700 text-white hover:bg-brand-800 transition-colors disabled:opacity-60"
            data-testid="pricing-modal-save"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save changes
          </button>
        </div>
      </div>
    </div>
  );
}
